/**
 * FsmToolbar — the FSM panel-header tool buttons (phase 6d, step 2).
 *
 * Direct port of `FSMHeaderView` (components/FSM/index.js): the armed-tool
 * buttons that drive the FSM `ui` store (add-initial-state / add-state /
 * add-final-state / add-transition), plus the action buttons (add-FSM,
 * calculate-FSM, copy-FSM, check-FSM, save-image).
 *
 * The armed-tool buttons read + write the SAME observable `ui` store the old FSM
 * used (`ui.activeTool`, `ui.toggleTool`), so arming a tool flips `activeTool`
 * exactly as before — which is what FsmFlow's pane/connect/drag gating keys off.
 * They are DISABLED when the visible FSM is read-only (`canEdit === false`, i.e. a
 * code-generation DEFAULT FSM), matching the old header.
 *
 * Reuses the SAME `Toolbar` UI component (variant="panel") + the SAME icons the
 * old FSM header used, so the control looks identical.
 *
 * Classic JSX runtime (app-wide esbuild transform) needs React in scope.
 */
import React from 'react';
import { observer } from 'mobx-react';
import Toolbar from '../components/Toolbar';
import {
  Picture as PictureIcon,
  AddFSM as AddFSMIcon,
  CopyFSM as CopyFSMIcon,
  CheckFSM as CheckFSMIcon,
  CalculateFSM as CalculateFSMIcon,
  AddInitialState as AddInitialStateIcon,
  AddState as AddStateIcon,
  AddFinalState as AddFinalStateIcon,
  AddTransition as AddTransitionIcon,
  SelectCodeGeneration as SelectCodeGenerationIcon,
} from '../components/Icons';

export type FsmToolbarProps = {
  /** The FSM UI store (`store.ui.fsm`) — the SAME observable the old FSM read. */
  // The legacy MST stores are untyped .js; `any` is the honest boundary type.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ui: any;
  /** Is an FSM visible (vs the empty view)? */
  hasFSM: boolean;
  /** Is the visible FSM editable (not a DEFAULT code-generation FSM)? */
  canEdit: boolean;
  /** Add an FSM to the object type of the current FSM, or undefined to hide. */
  onAddFSM?: () => void;
  /** Add a calculated FSM, or undefined when not possible (hides nothing — disabled). */
  onCalculateFSM?: () => void;
  /** Copy the current FSM. */
  onCopyFSM?: () => void;
  /** Check the FSM. */
  onCheckFSM?: () => void;
  /** Is the visible FSM the one selected for code generation on its object type? */
  codeGenerationSelected?: boolean;
  /** Select the visible FSM for code generation (deselects the object type's sibling). */
  onSelectForCodeGeneration?: () => void;
  /** Export the visible FSM as an image (the PNG-export trigger; 6e keeps it working). */
  onExportImage?: () => void;
  /** Extra class for the toolbar wrapper. */
  className?: string;
};

const FsmToolbar = observer(function FsmToolbar({
  ui,
  hasFSM,
  canEdit,
  onAddFSM,
  onCalculateFSM,
  onCopyFSM,
  onCheckFSM,
  codeGenerationSelected,
  onSelectForCodeGeneration,
  onExportImage,
  className,
}: FsmToolbarProps) {
  // The armed-tool buttons + action buttons — exactly the old header's button
  // list, in the SAME order. When no FSM is visible the header showed NO buttons.
  const buttons = hasFSM
    ? [
        {
          key: 'add initial state',
          icon: AddInitialStateIcon,
          title: 'Add initial state',
          handler: () => ui.toggleTool('add_initial_state'),
          disabled: !canEdit,
          active: ui.activeTool === 'add_initial_state',
        },
        {
          key: 'add state',
          icon: AddStateIcon,
          title: 'Add state',
          handler: () => ui.toggleTool('add_state'),
          disabled: !canEdit,
          active: ui.activeTool === 'add_state',
        },
        {
          key: 'add final state',
          icon: AddFinalStateIcon,
          title: 'Add final state',
          handler: () => ui.toggleTool('add_final_state'),
          disabled: !canEdit,
          active: ui.activeTool === 'add_final_state',
        },
        {
          key: 'add transition',
          icon: AddTransitionIcon,
          title: 'Add transition',
          handler: () => ui.toggleTool('add_transition'),
          disabled: !canEdit,
          active: ui.activeTool === 'add_transition',
        },
        {
          key: 'add fsm',
          icon: AddFSMIcon,
          title: 'Add FSM',
          handler: onAddFSM,
          active: false,
        },
        {
          key: 'calculate fsm',
          icon: CalculateFSMIcon,
          title: 'Calculate FSM',
          handler: onCalculateFSM,
          active: false,
          disabled: onCalculateFSM == null,
        },
        {
          key: 'copy fsm',
          icon: CopyFSMIcon,
          title: 'Copy FSM',
          handler: onCopyFSM,
          active: false,
        },
        {
          key: 'check fsm',
          icon: CheckFSMIcon,
          title: 'Check FSM',
          handler: onCheckFSM,
          active: false,
        },
        // Select-for-code-generation toggle — the same affordance the FSM
        // inspector exposes (`SelectForCodeGeneration`), surfaced directly in the
        // FSM window. Highlighted (`active`) + non-clickable (`disabled`) once
        // selected, mirroring the inspector's read-only "Selected for
        // codegeneration" state; only one FSM per object type can be selected, so
        // the command deselects the sibling for us.
        {
          key: 'select for code generation',
          icon: SelectCodeGenerationIcon,
          title: codeGenerationSelected
            ? 'Selected for code generation'
            : 'Select for code generation',
          handler: onSelectForCodeGeneration,
          active: codeGenerationSelected === true,
          disabled: codeGenerationSelected === true || onSelectForCodeGeneration == null,
        },
        // The "Save image" button — last in the old FSM header. Only rendered when
        // an `onExportImage` handler is supplied (the real project layout).
        ...(onExportImage
          ? [
              {
                key: 'export picture',
                icon: PictureIcon,
                title: 'Save image',
                handler: onExportImage,
                active: false,
              },
            ]
          : []),
      ]
    : [];

  return <Toolbar variant="panel" buttons={buttons} className={className} />;
});

export default FsmToolbar;
